import { deprecationWarning, ROOT_NODE, useEffectOnce } from '@ws-ui/craftjs-utils';
import React, { useRef } from 'react';

import { useInternalEditor } from '../editor/useInternalEditor';
import { SerializedNodes } from '../interfaces';
import { NodeElement } from '../nodes/NodeElement';

export type FrameProps = {
  children?: React.ReactNode;
  json?: string;
  data?: string | SerializedNodes;
};

const RenderRootNode = () => {
  const { timestamp, enabled } = useInternalEditor((state) => ({
    timestamp:
      state.nodes[ROOT_NODE] && state.nodes[ROOT_NODE]._hydrationTimestamp,
    enabled: state.options.enabled,
  }));

  if (!timestamp) {
    return null;
  }

  return <NodeElement id={ROOT_NODE} key={enabled ? timestamp : undefined} />;
};

/**
 * A React Component that defines the editable area
 */
export const Frame = ({ children, json, data }: FrameProps) => {
  const { actions, query } = useInternalEditor();

  if (!!json) {
    deprecationWarning('<Frame json={...} />', {
      suggest: '<Frame data={...} />',
    });
  }

  const isLoaded = useRef(false);

  useEffectOnce(() => {
    if (!isLoaded.current) {
      const initialData = data || json;

      if (initialData) {
        actions.history.ignore().deserialize(initialData);
      } else if (children) {
        const rootNode = React.Children.only(children) as React.ReactElement;

        const node = query
          .parseReactElement(rootNode)
          .toNodeTree((node, jsx) => {
            if (jsx === rootNode) {
              node.id = ROOT_NODE;
            }
            return node;
          });

        actions.history.ignore().addNodeTree(node);
      }

      isLoaded.current = true;
    }
  });

  return <RenderRootNode />;
};
